/**
 * Presentation vocabulary for the hazard UI (SPEC_Hazard_UI).
 *
 * Three distinctions live here because getting any of them wrong changes what
 * the curator concludes, not just how it looks:
 *
 *  - Source vs. barrier (§2.1). Roughly a third of the regulated types are pure
 *    barriers; if that is not visible, "Brandmeldeanlage: Brandeinwirkung" reads
 *    as a data error. Barriers are outlined, sources filled.
 *  - Provisional (§6). Stated in words, not drawn. Hatching was tried and
 *    dropped: while nothing is confirmed it covers every value, and a marker
 *    carried by all of them distinguishes nothing. A colour is ruled out by the
 *    spec anyway — provisional red and confirmed red are the same finding with
 *    different weight, and a fifth colour would read as a fifth state.
 *  - Not applicable vs. not assessable (§7). Empty means the type carries no
 *    hazard of that damage type; grey means the assessment is missing. Folding
 *    them together makes both read as "fine".
 */

import React from "react";

const badgeStyle = (background: string, color: string): React.CSSProperties => ({
  display: "inline-block",
  padding: "1px 8px",
  borderRadius: "10px",
  fontSize: "11px",
  fontWeight: 600,
  whiteSpace: "nowrap",
  backgroundColor: background,
  color,
});

/** Person first — every ordering in this UI leads with it. */
export const DAMAGE_TYPES = ["person", "umwelt", "sach", "recht"] as const;
export type DamageType = (typeof DAMAGE_TYPES)[number];

const DAMAGE_TYPE_COLORS: Record<string, { background: string; color: string }> = {
  person: { background: "#fde7e9", color: "#a4262c" },
  umwelt: { background: "#dff6dd", color: "#0b6a0b" },
  sach: { background: "#deecf9", color: "#004578" },
  recht: { background: "#efe5fb", color: "#5b2e91" },
};

const neutral = { background: "#f3f2f1", color: "#444" };

/**
 * Textfarben, die nicht zur Datenvisualisierung gehören, laufen über die
 * Oberflächen-Tokens: die Fachfarben oben kodieren Schadensart und Freigabe-
 * stand und bleiben wertgleich, ein grauer Hilfstext kodiert nichts und darf
 * deshalb den Kontrast nicht unterschreiten.
 */
const MUTED_TEXT = "hsl(var(--muted-foreground))";
const WARNING_TEXT = "hsl(var(--warning))";

export const damageTypeLabel = (t: (key: string) => string, damageType?: string | null): string =>
  damageType ? t(`hazard.damageType.${damageType}`) : "";

/**
 * One assigned hazard. `relation` drives the fill: a filled chip means the type
 * carries the hazard, an outlined one that it protects against it.
 */
export const HazardChip = ({
  label,
  damageType,
  relation,
  t,
  onClick,
}: {
  label: string;
  damageType?: string | null;
  relation: "carries" | "protects" | string;
  t: (key: string) => string;
  onClick?: () => void;
}) => {
  const colors = DAMAGE_TYPE_COLORS[damageType || ""] || neutral;
  const isBarrier = relation === "protects";
  const style: React.CSSProperties = {
    ...badgeStyle(isBarrier ? "transparent" : colors.background, colors.color),
    border: `1px solid ${isBarrier ? colors.color : "transparent"}`,
    cursor: onClick ? "pointer" : "default",
    marginRight: "4px",
    marginBottom: "3px",
  };
  const title = `${label} · ${damageTypeLabel(t, damageType)} · ${
    isBarrier ? t("hazard.relation.protects") : t("hazard.relation.carries")
  }`;

  const content = (
    <>
      {isBarrier ? <span style={{ marginRight: "3px" }}>⛉</span> : null}
      {label}
    </>
  );

  return onClick ? (
    <button type="button" style={style} title={title} onClick={onClick}>
      {content}
    </button>
  ) : (
    <span style={style} title={title}>
      {content}
    </span>
  );
};

/** Legend for the chips — once above the list, never per row (§2.1). */
export const HazardChipLegend = ({ t }: { t: (key: string) => string }) => (
  <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
    <span className="inline-flex items-center gap-1.5">
      <span style={badgeStyle("#f3f2f1", "#444")}>{t("hazard.legend.sourceExample")}</span>
      {t("hazard.legend.source")}
    </span>
    <span className="inline-flex items-center gap-1.5">
      <span style={{ ...badgeStyle("transparent", "#444"), border: "1px solid #444" }}>
        ⛉ {t("hazard.legend.barrierExample")}
      </span>
      {t("hazard.legend.barrier")}
    </span>
    <span className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 border-l pl-3">
      {DAMAGE_TYPES.map((damageType) => (
        <span key={damageType} className="inline-flex items-center gap-1">
          <span
            aria-hidden
            className="inline-block h-2 w-2 shrink-0 rounded-full"
            style={{ backgroundColor: DAMAGE_TYPE_COLORS[damageType].color }}
          />
          {damageTypeLabel(t, damageType)}
        </span>
      ))}
    </span>
  </div>
);

// „stale“ trug #8a6d00 auf #fff4ce — 4.47:1 und damit unter AA. Die Fläche
// bleibt (gelb = Freigabe veraltet), die Schrift läuft über das warning-Token
// (4.59:1).
const SIGNOFF_COLORS: Record<string, { background: string; color: string }> = {
  released: { background: "#dff6dd", color: "#0b6a0b" },
  expired: { background: "#fde7e9", color: "#a4262c" },
  stale: { background: "#fff4ce", color: WARNING_TEXT },
  never: { background: "#f3f2f1", color: "#444" },
};

/**
 * The four release states of §2.2. "stale" and "never" lead to different review
 * modes (delta vs. full), so they stay separate pills rather than collapsing
 * into one "offen".
 */
export const SignoffPill = ({ state, t }: { state?: string | null; t: (key: string) => string }) => {
  const colors = SIGNOFF_COLORS[state || "never"] || neutral;
  return (
    <span style={badgeStyle(colors.background, colors.color)}>
      {t(`hazard.signoff.${state || "never"}`)}
    </span>
  );
};

/**
 * One damage-type cell of the severity column.
 *
 * `count === 0` renders nothing: the type carries no hazard of this damage type,
 * which is information, not a task. A hazard without a severity renders grey and
 * clickable — that is an open task and has to look like one (§7).
 */
export const SeverityCell = ({
  damageType,
  entry,
  t,
  onClick,
}: {
  damageType: string;
  entry?: { siBase: number | null; assessable: boolean; count: number; provisional?: boolean };
  t: (key: string) => string;
  onClick?: () => void;
}) => {
  if (!entry || entry.count === 0) {
    return null;
  }

  const colors = DAMAGE_TYPE_COLORS[damageType] || neutral;
  const prefix = t(`hazard.damageTypeShort.${damageType}`);

  if (!entry.assessable) {
    // Graue Fläche bleibt (grau = Bewertung fehlt); die Schrift lief mit
    // #767676 bei 4.06:1 und steht jetzt auf dem muted-foreground-Token
    // (6.78:1).
    const style: React.CSSProperties = {
      ...badgeStyle("#f3f2f1", MUTED_TEXT),
      border: "1px dashed #b3b3b3",
      cursor: onClick ? "pointer" : "default",
      marginRight: "4px",
    };
    const label = `${prefix} ${t("hazard.severity.notAssessableShort")}`;
    return onClick ? (
      <button type="button" style={style} title={t("hazard.severity.notAssessableHint")} onClick={onClick}>
        {label}
      </button>
    ) : (
      <span style={style} title={t("hazard.severity.notAssessableHint")}>
        {label}
      </span>
    );
  }

  return (
    <span
      style={{ ...badgeStyle(colors.background, colors.color), marginRight: "4px" }}
      title={
        entry.provisional
          ? `${damageTypeLabel(t, damageType)} · ${t("hazard.severity.provisional")}`
          : damageTypeLabel(t, damageType)
      }
    >
      {prefix} {entry.siBase}
    </span>
  );
};

/** The compact `P 90 · U 40 · S 20` of §2.1. */
export const SeveritySummary = ({
  summary,
  t,
  onClick,
}: {
  summary?: Record<string, { siBase: number | null; assessable: boolean; count: number }>;
  t: (key: string) => string;
  onClick?: () => void;
}) => {
  if (!summary || Object.keys(summary).length === 0) {
    // #999 auf Weiß lag bei 2.85:1 — der Gedankenstrich läuft jetzt über das
    // muted-foreground-Token.
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  return (
    <span>
      {DAMAGE_TYPES.map((damageType) => (
        <SeverityCell
          key={damageType}
          damageType={damageType}
          entry={summary[damageType]}
          t={t}
          onClick={onClick}
        />
      ))}
    </span>
  );
};

export const formatDate = (value: any): string => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString();
};

/** Days until a date; negative once it has passed. */
export const daysUntil = (value: any): number | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return Math.floor((date.getTime() - Date.now()) / 86400000);
};
